import { PageContextProps } from 'application/contexts/PageContext';
import { legacyTransform as legacyTransformForComparisonFilterTable } from 'features/Modules/ComparisonFilterTable/legacyTransform';
import { legacyTransform as legacyTransformForComparisonTable } from 'features/Modules/ComparisonTable/legacyTransform';
import jsonpath from 'jsonpath';
import { queryCDA } from './queryCDA';

const getFlowSteps = async (module, pageContext) => {
	try {
		const steps = await queryCDA({
			pageContext,
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				fetch: `children:${module?.content?.properties?.flowData?.[0].id}`,
				expand: '$all',
				sort: 'sortOrder:asc',
				take: '200',
			},
			requestType: 'pageContent',
		});
		return steps;
	} catch (error) {
		throw `ERROR: getFlowSteps - module id: ${module.content.id} - ` + error;
	}
};

const getComparisonFilterTable = async (module, pageContext) => {
	try {
		const data = await queryCDA({
			pageContext,
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				fetch: `descendants:${module?.content?.properties?.data?.[0].id}`,
				expand: 'properties:$all',
				take: '200',
			},
			requestType: 'pageContent',
		});
		return data;
	} catch (error) {
		throw `ERROR: getComparisonFilterTable - module id: ${module.content.id} - ` + error;
	}
};

const getComparisonTable = async (module, pageContext) => {
	try {
		const data = await queryCDA({
			pageContext,
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				fetch: `descendants:${module?.content?.properties?.comparisonGroupFolder?.[0].id}`,
				expand: 'properties:$all',
				take: '200',
			},
			requestType: 'pageContent',
		});
		return data;
	} catch (error) {
		throw `ERROR: getComparisonTable - module id: ${module.content.id} - ` + error;
	}
};

export const getPageSectionContent = async (pageSection: Content.PageContentSections['pageSection'], pageContext: Partial<PageContextProps>) => {
	if (!pageSection?.items) return null;

	try {
		const flowModules = jsonpath.query(pageSection, '$..items[?(@.content.contentType=="flowModule")]');
		if (flowModules.length > 0) {
			await Promise.all(
				flowModules.map(async (module) => {
					const steps = await getFlowSteps(module, pageContext);

					jsonpath.value(
						pageSection,
						`$..items[?(@.content.id=="${module.content.id}")].content.properties.flowData[0].properties`,
						steps.items,
					);
				}),
			);
		}

		const comparisonFilterTables = jsonpath.query(pageSection, '$..items[?(@.content.contentType=="comparisonFilterTable")]');
		if (comparisonFilterTables.length > 0) {
			await Promise.all(
				comparisonFilterTables.map(async (module) => {
					const comparisonFilterTable = await getComparisonFilterTable(module, pageContext);
					const transformContent = legacyTransformForComparisonFilterTable(comparisonFilterTable?.items);

					jsonpath.value(
						pageSection,
						`$..items[?(@.content.id=="${module.content.id}")].content.properties.data[0].properties`,
						transformContent,
					);
				}),
			);
		}

		const comparisonTables = jsonpath.query(pageSection, '$..items[?(@.content.contentType=="comparisonTable")]');
		if (comparisonTables.length > 0) {
			await Promise.all(
				comparisonTables.map(async (module) => {
					const comparisonTable = await getComparisonTable(module, pageContext);
					const transformContent = legacyTransformForComparisonTable(comparisonTable?.items);

					jsonpath.value(
						pageSection,
						`$..items[?(@.content.id=="${module.content.id}")].content.properties.comparisonGroupFolder[0].properties`,
						transformContent,
					);
				}),
			);
		}

		return pageSection;
	} catch (error) {
		console.log('ERROR in getPageSectionContent - ', error);
		return pageSection;
	}
};
